import { githubColorTokenFromName } from '../githubColors.js'
import { appPageBoilerplateFetch } from '../pageBoilerplate.js'
import { avatarUrl } from '../../../shared/functions/peopleUtils.js'

// Re-export so existing tickets imports keep working without reaching
// across packages.
export { githubColorTokenFromName as githubColorToken }

// Visual lookup tables shared by TicketCard + TicketDetailModal.
export const PRIORITY_COLOR = {
  urgent: 'red', P0: 'red', P1: 'orange',
  high: 'orange', medium: 'blue', P2: 'blue',
  low: 'caption', P3: 'caption'
}

export const ISSUE_TYPE_COLOR = {
  Bug: 'red',
  Feature: 'green',
  Task: 'blue',
  Enhancement: 'green',
  'User Story': 'purple',
  Epic: 'orange'
}

export const STATE_COLOR = {
  OPEN: 'green',
  CLOSED: 'caption',
  MERGED: 'purple'
}

// Color tokens for internal ticket workflow states.
export const TICKET_STATE_COLOR = {
  not_started: 'caption',
  in_progress: 'yellow',
  blocked: 'red',
  ready_to_test: 'magenta',
  awaiting_approval: 'orange',
  done: 'green',
  closed: 'caption'
}

// Human-readable labels for all canonical ticket states.
// Falls back to title-cased raw value so new states degrade gracefully.
const STATE_LABEL = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  blocked: 'Blocked',
  ready_to_test: 'Ready to Test',
  awaiting_approval: 'Awaiting Approval',
  done: 'Done',
  closed: 'Closed'
}

export const formatStateLabel = (state) => {
  if (!state) return ''
  if (STATE_LABEL[state]) return STATE_LABEL[state]
  // Graceful fallback: replace underscores + title-case each word
  return state.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

// Resolve a ticket's assignees into a list ready for avatar rendering.
// Prefers people we know about (so the popover gets the real name),
// falls back to the raw GitHub login + GitHub avatar URL when the user
// only exists upstream. Used by TicketCard + TicketDetailModal.
export const resolveAssignees = (ticket, people) => {
  const out = []
  const seen = new Set()
  // Build a one-shot map for the call so the two passes below don't
  // re-walk `people` per assignee. WeakMap caching only kicks in when
  // the caller passes `root.people` directly — this local map handles
  // the smaller per-call list either way.
  const list = people || []
  const byEmail = new Map()
  const byGithub = new Map()
  for (const p of list) {
    if (p.email) byEmail.set(p.email, p)
    if (p.github) byGithub.set(p.github, p)
  }
  if (ticket?.assigneeEmail) {
    const p = byEmail.get(ticket.assigneeEmail)
    out.push({
      key: ticket.assigneeEmail,
      name: p?.name || ticket.assigneeEmail,
      avatar: p?.avatar || avatarUrl(p?.name || ticket.assigneeEmail),
      login: p?.github
    })
    seen.add(ticket.assigneeEmail)
    if (p?.github) seen.add(p.github)
  }
  for (const login of (ticket?.assigneeLogins || [])) {
    if (!login || seen.has(login)) continue
    const p = byGithub.get(login)
    if (p?.email && seen.has(p.email)) continue
    out.push({
      key: login,
      name: p?.name || login,
      avatar: `https://github.com/${login}.png?size=64`,
      login
    })
    seen.add(login)
    if (p?.email) seen.add(p.email)
  }
  return out
}

// Look up a column option id by display label. Avoids hardcoding the
// GitHub Status field option ids (`'4d6ab783'` etc) which can change
// when the upstream project re-seeds.
export const columnKeyByLabel = (root, label) => {
  const c = (root?.columns || []).find(c => c.label === label)
  return c?.key || null
}

// `ticket_columns` fetch — tickets-specific. Stacked on top of the
// cross-app `appPageBoilerplateFetch` from shared-gov so every tickets
// page gets `app_settings`, `people`, AND `ticket_columns` in one
// spread.
export const ticketColumnsFetch = {
  method: 'select',
  from: 'workspaceProject.ticketColumns',
  order: 'position',
  as: '~/columns',
  cache: '5m',
  transform: (rows) => ({ columns: (rows || []).map(rowToColumn) })
}

// Tickets-flavoured page boilerplate fetch — appPageBoilerplateFetch
// (shared with chat + calendar) plus the ticket_columns table that
// every tickets view needs.
export const pageBoilerplateFetch = [ticketColumnsFetch]


const _foldFieldValues = (fvs) => {
  const out = {}
  for (const v of fvs || []) {
    const name = v.project_fields?.name
    if (!name) continue
    let value = null
    if (v.option_name != null) value = v.option_name
    else if (v.number_value != null) value = v.number_value
    else if (v.date_value != null) value = v.date_value
    else if (v.text_value != null) value = v.text_value
    if (value != null) out[name] = value
  }
  return out
}

export const rowToTicket = (r) => {
  const customFields = _foldFieldValues(r.ticket_field_values)
  return {
    id: r.id,
    title: r.title,
    body: r.body,
    columnKey: r.column_key,
    position: r.position,
    assigneeEmail: r.assignee_email,
    assigneeLogins: r.assignee_logins || [],
    reporterEmail: r.reporter_email,
    labels: r.labels || [],
    // Priority comes from the GitHub Project's custom field — fall back
    // to the column on the tickets table if a future migration adds it.
    priority: customFields.Priority || r.priority || null,
    dueDate: customFields['Due Date'] || r.due_date,
    githubUrl: r.github_issue_url,
    githubNodeId: r.github_node_id,
    // Mirrored Projects v2 metadata (see migrations 0014 / 0016).
    number: r.number,
    contentType: r.content_type,        // 'Issue' | 'PullRequest' | 'DraftIssue'
    state: r.state,                     // 'OPEN' | 'CLOSED' | 'MERGED'
    repository: r.repository,           // 'symbo-ls/smbls'
    milestone: r.milestone,
    iterationId: r.iteration_id,
    iterationTitle: r.iteration_title || customFields.Iteration,
    iterationStart: r.iteration_start,
    iterationEnd: r.iteration_end,
    trackedByCount: r.tracked_by_count || 0,
    tracksCount: r.tracks_count || 0,
    // Issues 2.0 — GitHub native issue type + the project-level Epic field.
    issueType: r.issue_type,
    issueTypeColor: r.issue_type_color,
    epicOptionId: r.epic_option_id,
    epicName: r.epic_name || customFields.Epic,
    parentIssueUrl: r.parent_issue_url,
    subIssuesTotal: r.sub_issues_total || 0,
    subIssuesCompleted: r.sub_issues_completed || 0,
    // Common Projects v2 custom fields, surfaced as first-class props
    // for the card UI. Anything not in this list is still available via
    // `customFields[name]`.
    severity: customFields.Severity || null,
    storyPoints: customFields['Story Points'] || null,
    size: customFields.Size || null,
    estimate: customFields.Estimate || null,
    environment: customFields.Environment || null,
    reviewStatus: customFields['Review Status'] || null,
    customFields,
    createdAt: r.created_at,
    updatedAt: r.updated_at
  }
}

export const rowToColumn = (r) => ({
  id: r.id,
  key: r.key,
  label: r.label,
  position: r.position,
  color: r.color,                  // GitHub color name (GRAY/GREEN/etc.)
  description: r.description,
  optionId: r.option_id
})

// Filter + sort tickets that belong to a given column. Honours the active
// filter slots on root state so the board re-renders reactively when the
// user toggles a chip.
//
// Memoised: a single pass over `root.tickets` builds the entire
// `columnKey → tickets[]` index, cached on a WeakMap keyed off the
// `root.tickets` array identity. Subsequent calls (12 columns × 2 cell
// renderers per render) hit the cache. The cache is invalidated when
// `root.tickets` is replaced wholesale (the smbls update pattern) or
// when the filter signature changes — both cheap O(1) checks.
const _columnIndexCache = new WeakMap()
const _EMPTY_LIST = Object.freeze([])

const _filterSignature = (root) =>
  `${root.filterAssignee || ''}|${root.filterLabel || ''}|${root.filterEpic || ''}|${root.filterIssueType || ''}|${(root.searchQuery || '').toLowerCase().trim()}|${root.currentUserEmail || ''}`

const _buildColumnIndex = (root) => {
  const me = root.currentUserEmail
  const assignee = root.filterAssignee
  const label = root.filterLabel
  const epic = root.filterEpic
  const issueType = root.filterIssueType
  const q = (root.searchQuery || '').trim().toLowerCase()
  const byCol = new Map()
  for (const t of (root.tickets || [])) {
    if (assignee === 'me') { if (t.assigneeEmail !== me) continue }
    else if (assignee && assignee !== 'all') { if (t.assigneeEmail !== assignee) continue }
    if (label && !(t.labels || []).includes(label)) continue
    if (epic && t.epicOptionId !== epic) continue
    if (issueType && t.issueType !== issueType) continue
    if (q && !((t.title || '').toLowerCase().includes(q) || (t.body || '').toLowerCase().includes(q))) continue
    let arr = byCol.get(t.columnKey)
    if (!arr) { arr = []; byCol.set(t.columnKey, arr) }
    arr.push(t)
  }
  for (const arr of byCol.values()) {
    arr.sort((a, b) => (a.position || 0) - (b.position || 0))
  }
  return byCol
}

export function getTicketsForColumn (root, columnKey) {
  if (!root || !root.tickets) return _EMPTY_LIST
  let entry = _columnIndexCache.get(root.tickets)
  const sig = _filterSignature(root)
  if (!entry || entry.sig !== sig) {
    entry = { sig, byCol: _buildColumnIndex(root) }
    _columnIndexCache.set(root.tickets, entry)
  }
  return entry.byCol.get(columnKey) || _EMPTY_LIST
}

// Compute a fractional `position` value for a ticket dropped between two
// neighbours. Returns the midpoint so we never have to renumber the column.
export function positionBetween (prev, next) {
  if (prev == null && next == null) return 1000
  if (prev == null) return next - 1000
  if (next == null) return prev + 1000
  return (prev + next) / 2
}

// Convert a column key into the brand-aligned colour token used by
// TicketCard's status accent stripe. Hoisted to module scope so the
// hot-path map literal isn't allocated on every call.
const COLUMN_ACCENT = {
  backlog: 'caption',
  todo: 'blue',
  in_progress: 'orange',
  review: 'purple',
  done: 'green'
}

export function getColumnAccent (columnKey) {
  return COLUMN_ACCENT[columnKey] || 'caption'
}

// GitHub Project 5 status column ids that count as "currently being
// worked on" — used by the My View page to bucket assigned tickets into
// "active" vs "backlog" rails. Hoisted so the lookup set isn't
// reallocated on every filter pass.
//   4d6ab783 = In Progress
//   821cd85d = In Review
//   20f135e3 = Ready For QA
//   d677f7ee = In QA
//   2ea8dd8b = Blocked for DEV (still considered active)
const ACTIVE_COLUMN_KEYS = new Set([
  '4d6ab783', '821cd85d', '20f135e3', 'd677f7ee', '2ea8dd8b'
])

// GitHub Project 5 "Done" column id — used by the Epics index hero
// stat to compute the % done across the project.
export const DONE_COLUMN_KEY = 'eede5560'

export const isActiveColumn = (columnKey) => ACTIVE_COLUMN_KEYS.has(columnKey)

// Map a tickets-table row → the StoryCard payload that
// shared-gov/StoriesSection renders. We label each ticket as a
// pseudo-epic with one column-key bucket so the breakdown bar shows
// the status colour for that single ticket. Pass an optional `people`
// array to surface the assignee's display name in the description
// (used by the General page's recent-activity feed).
export const ticketToStoryCard = (cols, t, people) => {
  const col = (cols || []).find(c => c.key === t.column_key) || {}
  let description = t.epic_name
    ? `${t.repository || ''} · ${col.label || ''} · ${t.epic_name}`
    : `${t.repository || ''} · ${col.label || ''}`
  if (people) {
    const who = t.assignee_email
      ? ((people.find(p => p.email === t.assignee_email)?.name) || t.assignee_email)
      : 'Unassigned'
    description = `${description} · ${who}`
  }
  return {
    id: t.id,
    title: `#${t.number || t.id} · ${t.title}`,
    name: `#${t.number || t.id} · ${t.title}`,
    description,
    color: col.color || 'GRAY',
    isTicketCard: true
  }
}

// Build a fresh newTicket payload, merging caller overrides on top of a
// clean baseline so previously-typed fields don't leak between modal opens.
// agentTest: true means the QA agent will auto-test this ticket after ship.
export function freshTicket (overrides = {}) {
  return {
    title: '',
    body: '',
    columnKey: 'backlog',
    assigneeEmail: '',
    labels: [],
    priority: 'medium',
    issueType: '',
    storyPoints: '',
    acceptanceCriteria: '',
    stepsToReproduce: '',
    expectedResult: '',
    dueDate: '',
    editingTicketId: null,
    agentTest: true,
    ...overrides
  }
}
